import React from 'react';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { select } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import { Icon } from './CollapsedWidget';

const OpenWidget = ({ widget, isSelected, isParticular }) => {
  const { t } = useTranslation();
  const {
    icon,
    notifications,
    ctaCallback,
    text,
    completeText,
    completeTitle,
    ctaText,
    name,
    linkText,
    linkCallback
  } = widget;

  const cookieExportPostingsQualityToExcel = Cookies.get('exportPostingsQualityToExcel');

  const showLinkTextCTA = () => {
    if (name === 'quality' && !isParticular && notifications > 0 && linkText && linkCallback) {
      return true;
    }
    if (name === 'reports' && notifications === 0 && linkText && linkCallback) {
      return true;
    }
    return false;
  };

  return (
    <Container notifications={notifications} data-panel={`panel-dashboard-openWidget-${name}`}>
      <Box notifications={notifications} isSelected={isSelected}>
        <NotificationsArea>
          <Icon
            widget={icon}
            notifications={notifications}
            isOpen
            data-panel={`panel-dashboard-openWidget-Icon-${icon}`}
          />
          {notifications > 0 && (
            <NotificationNumber data-panel={`panel-dashboard-openWidget-notifications-${name}`}>
              {notifications < 100 ? notifications : '+99'}
            </NotificationNumber>
          )}
        </NotificationsArea>
        <InfoArea>
          {notifications > 0 ? (
            <CTA data-panel={`panel-dashboard-openWidget-CTA-${name}`}>
              <LinkCTA isSelected={isSelected} onClick={() => notifications > 0 && ctaCallback()}>
                {t(ctaText)}
              </LinkCTA>
            </CTA>
          ) : (
            <CompleteTitle data-panel={`panel-dashboard-openWidget-completeTitle-${name}`}>
              {t(completeTitle)}
            </CompleteTitle>
          )}

          <TextContent data-panel={`panel-dashboard-openWidget-text-${name}`}>
            {notifications > 0 ? t(text) : t(completeText)}
            {showLinkTextCTA() && (
              <SimpleTooltip
                showChildren={name === 'quality'}
                offset={10}
                delay={100}
                delayOut={200}
                position='bottom-center'
                text={
                  !cookieExportPostingsQualityToExcel
                    ? t('panel.quality.generalData.CTA.tooltip')
                    : t('panel.quality.generalData.CTA.tooltip.success')
                }
                dataPanel='tooltip-interactive-link'
                width='225px'
              >
                <LinkTextCTA
                  downloaded={name === 'quality' && cookieExportPostingsQualityToExcel}
                  className='interactive-link'
                  onClick={linkCallback}
                  aria-hidden='true'
                  data-panel='cta-interactive-link'
                >
                  {t(linkText)}
                </LinkTextCTA>
              </SimpleTooltip>
            )}
          </TextContent>
        </InfoArea>
      </Box>
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
`;

const Box = styled.div`
  display: flex;
  height: 125px;
  width: 297px;
  border-radius: 12px;
  padding: 23.5px 16px 21px 25px;
  background-color: 'none';

  @media (max-width: 1260px) {
    width: 245px;
    padding: 8px 16px 8px 16px;
  }
  @media (max-width: 1080px) {
    width: 235px;
    padding: 4px 8px 4px 6px;
  }
`;
const NotificationsArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin-right: 28px;
  @media (max-width: 1260px) {
    margin-right: 16px;
    padding: 16px 0;
  }
  @media (max-width: 1080px) {
    margin-right: 8px;
  }
`;

const NotificationNumber = styled.div`
  font-family: Hind;
  font-size: 32px;
  font-weight: 700;
  line-height: 51px;
  color: ${select('darkGray')};
  position: relative;
  top: 12px;
`;

const InfoArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const CTA = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5.5px;
  cursor: pointer;
  margin-left: -4px;
`;

const LinkCTA = styled.a`
  display: inline-block;
  text-decoration: underline;
  font-family: Hind;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;

  white-space: pre-line;
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  color: ${getColor('primary')};
  background-color: ${({ isSelected }) => (isSelected ? select('middleGray') : 'none')};
  background: ${({ isSelected }) => (isSelected ? select('middleGray') : 'none')};
  :hover {
    background-color: ${select('middleGray')};
  }
`;

const TextContent = styled.div`
  font-family: Hind;
  font-size: 12px;
  font-weight: 500;
  line-height: 19px;
  color: ${select('darkGray')};
  white-space: pre-line;
  > .interactive-link {
    cursor: pointer;
    color: black;
    text-decoration: underline;
    margin-left: 4px;
  }
`;

const LinkTextCTA = styled.a`
  color: black;
  text-decoration: underline;
  margin-left: 4px;
  padding: 4px;
  border-radius: 8px;
  cursor: ${({ downloaded }) => (downloaded ? 'not-allowed' : 'pointer')};
  color: ${({ downloaded }) => downloaded && select('midDarkGray')};
  pointer-events: ${({ downloaded }) => downloaded && 'none'};
  :hover {
    background-color: ${select('middleGray')};
  }
`;

const CompleteTitle = styled.div`
  font-family: Hind;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: ${select('darkGray')};
  white-space: pre-line;
`;

export default OpenWidget;
